import { DocumentResult } from 'expo-document-picker';
import React, { useRef } from 'react';
import { toast } from 'react-toastify';

import { auth0Audience } from '../../api/API';
import { Environment } from '../../Environment';
import { Auth0Context, useAuth0 } from '../Auth0Provider';
import { ButtonState } from '../MNOHSButton';

type Files = {
  studentTranscriptData: DocumentResult | null;
  studentGpaAndRank: DocumentResult | null;
  studentGrade: DocumentResult | null;
};

export type StudentResultsUploadProps = {
  // inputs
  /** files picked */
  files: Files;
  /** button state */
  buttonState: ButtonState;

  // outputs
  /** invoked if user wants to upload a csv file */
  userChoseDocument: (files: Files) => void;
  /** invoked if user clicks "Process" button */
  userClickedProcess: () => void;
};

export const studentResultsUploadContainer = (Screen: React.ComponentType<StudentResultsUploadProps>) => () => {
  const initialFiles = {
    studentTranscriptData: null,
    studentGpaAndRank: null,
    studentGrade: null,
  };

  const [buttonState, setButtonState] = React.useState<ButtonState>('enabled');
  const [files, setFiles] = React.useState<Files>(initialFiles);

  const { getTokenSilently } = useAuth0() as Auth0Context;
  const { api } = Environment.current();

  // To prevent update on unmount
  const isMounted = useRef(true);
  React.useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <Screen
      files={files}
      buttonState={
        files.studentTranscriptData && files.studentGpaAndRank && files.studentGrade ? buttonState : 'disabled'
      }
      userChoseDocument={setFiles}
      userClickedProcess={async () => {
        try {
          if (!files.studentTranscriptData) throw new Error('You missed the csv file for Student Transcript Data!');
          if (!files.studentGpaAndRank) throw new Error('You missed the csv file for Student GPA and Rank!');
          if (!files.studentGrade) throw new Error('You missed the csv file for Student Grade!');

          setButtonState('showingActivityIndicator');

          const token = await getTokenSilently({
            audience: auth0Audience,
            scope: 'admin:students',
          });

          // @ts-ignore - TODO: find a way to actually tackle this
          const csvFiles: File[] = (Object.keys(files) as Array<keyof typeof files>).map(key => files[key].file);
          await api.bulkUploadStudentResults(csvFiles, token || '');

          if (isMounted.current) {
            toast.success(`Successfully sent bulk upload request!`);
            setButtonState('enabled');
            setFiles(initialFiles);
          }
        } catch (e) {
          toast.error(e.message);
          setButtonState('enabled');
        }
      }}
    />
  );
};
